import { useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Toggle } from '@/components/ui/Toggle';
import { describeError } from '@/features/auth/api';
import type { UserProfile } from '@/features/auth/session';
import { useChangePassword, useUpdateNotificationPreferences } from '../api';
import { ProfileCard } from './ProfileCard';

/**
 * Password and mail preferences.
 *
 * The toggles save on change rather than behind a "Kaydet": there are two of
 * them, each is reversible in one tap, and a save button for a switch is a
 * second thing to forget to press.
 */
export function PreferencesCard({ profile }: { profile: UserProfile }) {
  const [changingPassword, setChangingPassword] = useState(false);
  const preferences = useUpdateNotificationPreferences();

  function save(next: { emailNotifications?: boolean; promotionalEmails?: boolean }) {
    preferences.mutate({
      emailNotifications: next.emailNotifications ?? profile.emailNotificationsEnabled,
      promotionalEmails: next.promotionalEmails ?? profile.promotionalEmailsEnabled,
    });
  }

  return (
    <ProfileCard title="Tercihler & güvenlik">
      <div className="flex flex-col divide-y divide-surface-800/70">
        <div className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-surface-200">Parola</p>
              <p className="mt-0.5 font-mono text-xs text-surface-600">
                {profile.hasPassword ? '••••••••••' : 'Tanımlı değil'}
              </p>
            </div>

            {profile.hasPassword ? (
              <button
                type="button"
                onClick={() => setChangingPassword(!changingPassword)}
                aria-expanded={changingPassword}
                className="shrink-0 text-sm text-brand-400 transition hover:text-brand-300"
              >
                {changingPassword ? 'Vazgeç' : 'Değiştir'}
              </button>
            ) : (
              <span className="shrink-0 text-xs text-surface-600">Telegram ile giriş</span>
            )}
          </div>

          {changingPassword ? (
            <div className="mt-4">
              <PasswordForm onDone={() => setChangingPassword(false)} />
            </div>
          ) : null}
        </div>

        <div className="py-4">
          <Toggle
            label="Hesap e-postaları"
            // Says "güvenlik uyarıları hariç" because that is now literally true:
            // the payout-address alert ignores this switch. Whoever turned it off
            // may not be the member, and it is the only guard on that endpoint.
            description="Doğrulama ve bilgilendirme e-postaları. Güvenlik uyarıları hariç — onlar her zaman gönderilir."
            checked={profile.emailNotificationsEnabled}
            disabled={preferences.isPending}
            onChange={(checked) => save({ emailNotifications: checked })}
          />
        </div>

        <div className="pt-4">
          <Toggle
            label="Promosyon ve kampanyalar"
            description="Yeni bonuslar ve yarışma duyuruları."
            checked={profile.promotionalEmailsEnabled}
            disabled={preferences.isPending}
            onChange={(checked) => save({ promotionalEmails: checked })}
          />
        </div>
      </div>

      {preferences.isError ? (
        <Alert tone="error" className="mt-4">
          {describeError(preferences.error)}
        </Alert>
      ) : null}
    </ProfileCard>
  );
}

function PasswordForm({ onDone }: { onDone: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const change = useChangePassword();

  const mismatch = confirm !== '' && confirm !== newPassword;
  const ready = currentPassword !== '' && newPassword.length >= 10 && !mismatch && confirm !== '';

  if (change.isSuccess) {
    return (
      <Alert tone="success">Parolan değiştirildi. Diğer cihazlardaki oturumların kapatıldı.</Alert>
    );
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();

        if (!ready) {
          return;
        }

        change.mutate({ currentPassword, newPassword });
      }}
    >
      {change.isError ? <Alert tone="error">{describeError(change.error)}</Alert> : null}

      <Field
        label="Mevcut parolan"
        type="password"
        value={currentPassword}
        autoComplete="current-password"
        onChange={(event) => setCurrentPassword(event.target.value)}
      />

      <Field
        label="Yeni parola"
        type="password"
        value={newPassword}
        autoComplete="new-password"
        hint="En az 10 karakter. Uzun bir cümle, karmaşık bir kelimeden iyidir."
        onChange={(event) => setNewPassword(event.target.value)}
      />

      <Field
        label="Yeni parola (tekrar)"
        type="password"
        value={confirm}
        autoComplete="new-password"
        error={mismatch ? 'Parolalar eşleşmiyor.' : undefined}
        onChange={(event) => setConfirm(event.target.value)}
      />

      <p className="text-xs text-surface-500">
        Parolanı değiştirdiğinde diğer cihazlardaki oturumların kapanır. Bu sekme açık kalır.
      </p>

      <div className="flex gap-2">
        <Button type="submit" loading={change.isPending} disabled={!ready}>
          Parolayı değiştir
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Vazgeç
        </Button>
      </div>
    </form>
  );
}
